import type { ReactNode } from "react";
import {
  Controller,
  useFormContext,
  useWatch,
  type FieldPath,
} from "react-hook-form";
import { Flag } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { OptionCard } from "../../../common/OptionCard";
import { wizardLayout } from "../../wizardLayout";
import { Clubhouse_LgMatchPlayScoringMode } from "../../../../enums";
import type { AdvancedLeagueFormValues } from "../../advancedLeagueSchema";

type Name = FieldPath<AdvancedLeagueFormValues>;

// A rounded, bordered field-list — the same convention Round Settings / Side
// Contests use — with one row per line inside.
function FieldListGroup({ children }: { children: ReactNode }) {
  return (
    <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface">
      {children}
    </div>
  );
}

/** A labeled numeric field-list row: label left, small number input right. */
function PointRow({
  name,
  label,
  disabled = false,
}: {
  name: Name;
  label: string;
  disabled?: boolean;
}) {
  const { control } = useFormContext<AdvancedLeagueFormValues>();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div className="flex items-center gap-4 px-4 py-3">
          <label
            htmlFor={name}
            className={cn(
              "poppins text-sm font-semibold tracking-tight",
              disabled ? "text-text-muted" : "text-text-primary",
            )}
          >
            {label}
          </label>
          <Input
            id={name}
            type="number"
            min={0}
            disabled={disabled}
            aria-invalid={!!fieldState.error || undefined}
            value={field.value == null ? "" : String(field.value as number)}
            onChange={(e) =>
              field.onChange(
                e.target.value === "" ? undefined : Number(e.target.value),
              )
            }
            className="monoNumber w-18 rounded-md border-border bg-surface text-center font-semibold tabular-nums dark:bg-input"
          />
        </div>
      )}
    />
  );
}

// ─── Point Values (Points League) ────────────────────────────────────────────

const POINT_ROWS: { name: Name; label: string }[] = [
  {
    name: "scoringPolicy.pointsLeague.customPoints.doubleBogey",
    label: "Double Bogey+",
  },
  { name: "scoringPolicy.pointsLeague.customPoints.bogey", label: "Bogey" },
  { name: "scoringPolicy.pointsLeague.customPoints.par", label: "Par" },
  { name: "scoringPolicy.pointsLeague.customPoints.birdie", label: "Birdie" },
  {
    name: "scoringPolicy.pointsLeague.customPoints.eagle",
    label: "Eagle or better",
  },
];

export function PointValuesFields() {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-text-muted">
        Set the points awarded for each score type.
      </p>
      <FieldListGroup>
        {POINT_ROWS.map((r) => (
          <PointRow key={r.name} name={r.name} label={r.label} />
        ))}
      </FieldListGroup>
    </div>
  );
}

// ─── Side Contests (Points & Match) ──────────────────────────────────────────

const CONTESTS = [
  { key: "closestToPin", label: "Closest to Pin" },
  { key: "longDrive", label: "Long Drive" },
  { key: "skins", label: "Skins" },
] as const;

function ContestRow({
  contestKey,
  label,
}: {
  contestKey: (typeof CONTESTS)[number]["key"];
  label: string;
}) {
  const { control } = useFormContext<AdvancedLeagueFormValues>();
  const enabledName =
    `scoringPolicy.sideContests.${contestKey}.enabled` as Name;
  const pointsName = `scoringPolicy.sideContests.${contestKey}.points` as Name;
  // The point value stays in the payload when the contest is off, but its input
  // dims and disables so it reads as inactive rather than vanishing.
  const enabled = !!useWatch({ control, name: enabledName });
  return (
    <div className="flex items-center gap-4 px-4 py-3">
      <div className="flex items-center gap-3">
        <Controller
          name={enabledName}
          control={control}
          render={({ field }) => (
            <Switch
              id={enabledName}
              checked={!!field.value}
              onCheckedChange={field.onChange}
            />
          )}
        />
        <label
          htmlFor={pointsName}
          className={cn(
            "poppins text-sm font-semibold tracking-tight",
            enabled ? "text-text-primary" : "text-text-muted",
          )}
        >
          {label}
        </label>
      </div>
      <Controller
        name={pointsName}
        control={control}
        render={({ field, fieldState }) => (
          <Input
            id={pointsName}
            type="number"
            min={0}
            disabled={!enabled}
            aria-invalid={!!fieldState.error || undefined}
            value={field.value == null ? "" : String(field.value as number)}
            onChange={(e) =>
              field.onChange(
                e.target.value === "" ? undefined : Number(e.target.value),
              )
            }
            className={cn(
              "monoNumber w-16 rounded-md border-border text-center font-semibold tabular-nums",
              enabled
                ? "bg-surface dark:bg-input"
                : "bg-muted/40 text-text-muted",
            )}
          />
        )}
      />
    </div>
  );
}

export function SideContestsFields() {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-text-muted">
        Toggle a contest on to award bonus points for it during play; each has
        its own point value.
      </p>
      <FieldListGroup>
        {CONTESTS.map((c) => (
          <ContestRow key={c.key} contestKey={c.key} label={c.label} />
        ))}
      </FieldListGroup>
    </div>
  );
}

// ─── Match Type (Match Play) ─────────────────────────────────────────────────

const { HEAD_TO_HEAD, HOLE_BY_HOLE } = Clubhouse_LgMatchPlayScoringMode;

const iconTile = (child: ReactNode) => (
  <span className="flex size-9 items-center justify-center rounded-md bg-muted text-text-primary">
    {child}
  </span>
);

const MATCH_TYPE_CARDS = [
  {
    mode: HEAD_TO_HEAD,
    title: "Head-to-Head",
    description:
      "Winner is decided by the overall match result, not hole-by-hole.",
    icon: iconTile(<span className="poppins text-sm font-bold">VS</span>),
  },
  {
    mode: HOLE_BY_HOLE,
    title: "Hole-by-Hole",
    description:
      "Points are awarded separately for every hole won, tied, or lost.",
    icon: iconTile(<Flag className="size-5" />),
  },
];

const MATCH_RESULT_ROWS: { name: Name; label: string }[] = [
  { name: "scoringPolicy.matchPlay.winPoints", label: "Win Points" },
  { name: "scoringPolicy.matchPlay.tiePoints", label: "Tie Points" },
  { name: "scoringPolicy.matchPlay.lossPoints", label: "Loss Points" },
];

const HOLE_RESULT_ROWS: { name: Name; label: string }[] = [
  { name: "scoringPolicy.matchPlay.holeWonPoints", label: "Hole Won" },
  { name: "scoringPolicy.matchPlay.holeTiedPoints", label: "Hole Tied" },
  { name: "scoringPolicy.matchPlay.holeLostPoints", label: "Hole Lost" },
];

export function MatchTypeFields() {
  const { control, setValue } = useFormContext<AdvancedLeagueFormValues>();
  const mode = useWatch({
    control,
    name: "scoringPolicy.matchPlay.matchScoringMode",
  });
  const holeByHole = mode === HOLE_BY_HOLE;
  const rows = holeByHole ? HOLE_RESULT_ROWS : MATCH_RESULT_ROWS;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-text-muted">
        Choose how each match is decided, then set the points awarded for each
        outcome.
      </p>
      <div className={wizardLayout.choices}>
        {MATCH_TYPE_CARDS.map((c) => (
          <OptionCard
            key={c.mode}
            className={wizardLayout.choice}
            icon={c.icon}
            title={c.title}
            description={c.description}
            align="left"
            selected={mode === c.mode}
            onClick={() =>
              setValue("scoringPolicy.matchPlay.matchScoringMode", c.mode, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />
        ))}
      </div>
      {/* Keyed so the point set fades/slides in when the match type flips — the
          one authored moment for this interaction. */}
      <div
        key={holeByHole ? "hole" : "match"}
        className="flex flex-col gap-2 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1 motion-safe:duration-200 motion-safe:ease-out"
      >
        <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          {holeByHole ? "Hole Result Points" : "Match Result Points"}
        </p>
        <FieldListGroup>
          {rows.map((r) => (
            <PointRow key={r.name} name={r.name} label={r.label} />
          ))}
        </FieldListGroup>
      </div>
    </div>
  );
}
